import React, { useState, useEffect } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { trackOrder, printOrderReceipt, sendOrderReceipt, buildReceiptHtml, initiateToucanPayment } from '../marketApi';
import { useCart } from '../CartContext';
import { formatPrice } from '../currency';

function statusClass(status) {
  const s = (status || '').toLowerCase();
  if (s === 'delivered') return 'badge-success';
  if (s === 'cancelled') return 'badge-error';
  if (s === 'confirmed') return 'badge-blue';
  if (s === 'packed') return 'badge-purple';
  return 'badge-warning';
}

function groupBySeller(items) {
  const map = new Map();
  for (const item of (items || [])) {
    const key = String(item.seller_id || item.sellerId || 'unknown');
    const name = item.seller_name || item.sellerName || 'Seller';
    if (!map.has(key)) map.set(key, { sellerName: name, items: [], subtotal: 0 });
    const g = map.get(key);
    g.items.push(item);
    g.subtotal += (item.price || 0) * (item.quantity || 1);
  }
  return Array.from(map.values());
}

export default function OrderConfirmation() {
  const { ref } = useParams();
  const location = useLocation();
  const { clearCart } = useCart();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [emailInput, setEmailInput] = useState(location.state?.email || '');
  const [emailSending, setEmailSending] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null); // 'sent' | 'error' | null
  const [payLoading, setPayLoading]   = useState(false);
  const [payError, setPayError]       = useState('');

  // Read ToucanPay callback result from URL query params
  const qp            = new URLSearchParams(location.search);
  const paymentResult = qp.get('payment');       // 'success' | 'failed'
  const txnId         = qp.get('txnid');         // gateway transactionID
  const urlInvoiceNo  = qp.get('invoiceNo');     // Invoice / Order No
  const urlApproval   = qp.get('approvalCode');  // Approval Code
  const urlCardType   = qp.get('cardType');      // Card Type
  const urlSaleDate   = qp.get('saleDateTime');  // Payment Date/Time
  const actionCode    = qp.get('actionCode');    // gateway actionCode

  const isPaid = paymentResult === 'success' || order?.payment_status === 'paid';

  // Resolve gateway fields: prefer URL params (immediate), fall back to DB fields (after order loads)
  const gwTxnId      = txnId       || order?.transaction_id       || '';
  const gwInvoiceNo  = urlInvoiceNo || order?.gateway_invoice_no  || order?.gateway_order_no || '';
  const gwApproval   = urlApproval  || order?.gateway_approval_code || '';
  const gwCardType   = urlCardType  || order?.gateway_card_type   || '';
  const gwSaleDate   = urlSaleDate  || order?.gateway_sale_datetime || '';
  const gwAction     = actionCode   || order?.gateway_action_code || '';

  useEffect(() => {
    if (!ref) return;
    trackOrder(ref)
      .then((data) => {
        setOrder(data);
        setLoading(false);
      })
      .catch((e) => {
        setError(e.message || 'Could not fetch order details');
        setLoading(false);
      });
  }, [ref]);

  // Clear cart when returning from ToucanPay payment
  useEffect(() => {
    if (paymentResult === 'success') clearCart();
  }, [paymentResult]); // eslint-disable-line

  return (
    <div className="page-container" style={{ maxWidth: 640, margin: '0 auto' }}>

      {/* Order header */}
      <div style={{ textAlign: 'center', padding: '36px 0 24px' }}>
        <div className="success-check">✅</div>
        <h1 style={{ fontSize: 24, fontWeight: 800, marginBottom: 8 }}>
          {isPaid ? 'Order Placed & Payment Successful!' : 'Order Placed Successfully!'}
        </h1>
        <p style={{ color: 'var(--muted)', marginBottom: 16 }}>
          {isPaid
            ? 'Your payment was received and your order is confirmed.'
            : 'Your order has been received.' + (order && order.sellerCount > 1
                ? ` Items from ${order.sellerCount} sellers will be delivered separately.`
                : ' The seller will confirm it shortly.')}
        </p>
        <span className="order-ref">{ref}</span>
      </div>

      {loading ? (
        <div>
          <div className="skeleton" style={{ height: 16, marginBottom: 8 }} />
          <div className="skeleton" style={{ height: 16, width: '70%', marginBottom: 8 }} />
          <div className="skeleton" style={{ height: 16, width: '50%' }} />
        </div>
      ) : error ? (
        <div className="alert alert-warning">
          Order placed! Could not load details: {error}
        </div>
      ) : order ? (
        <div className="card" style={{ padding: 20, marginBottom: 20 }}>
          {/* Status */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 16,
            }}
          >
            <div style={{ fontWeight: 700, fontSize: 15 }}>Order Details</div>
            <span className={`badge ${statusClass(order.status)}`} style={{ fontSize: 13, padding: '4px 12px' }}>
              {(order.status || 'pending').toUpperCase()}
            </span>
          </div>

          {/* Buyer info */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16, fontSize: 14 }}>
            <div>
              <span style={{ color: 'var(--muted)', minWidth: 90, display: 'inline-block' }}>Name</span>
              <strong>{order.buyer_name || order.buyerName}</strong>
            </div>
            <div>
              <span style={{ color: 'var(--muted)', minWidth: 90, display: 'inline-block' }}>Phone</span>
              <strong>{order.buyer_phone || order.phone}</strong>
            </div>
            <div>
              <span style={{ color: 'var(--muted)', minWidth: 90, display: 'inline-block' }}>Address</span>
              <strong>{order.delivery_address || order.deliveryAddress}</strong>
            </div>
            {(order.city) && (
              <div>
                <span style={{ color: 'var(--muted)', minWidth: 90, display: 'inline-block' }}>City</span>
                <strong>{order.city}</strong>
              </div>
            )}
          </div>

          {/* Payment gateway details — shown immediately from URL params, enriched from DB */}
          {isPaid && (
            <>
              <hr className="divider" />
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, color: '#15803d', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span>✅</span> PAYMENT GATEWAY RECEIPT
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13 }}>
                  {gwTxnId && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--muted)' }}>Gateway Transaction ID</span>
                      <strong style={{ fontFamily: 'monospace' }}>{gwTxnId}</strong>
                    </div>
                  )}
                  {gwInvoiceNo && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--muted)' }}>Invoice / Order No</span>
                      <strong style={{ fontFamily: 'monospace' }}>{gwInvoiceNo}</strong>
                    </div>
                  )}
                  {gwApproval && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--muted)' }}>Approval Code</span>
                      <strong>{gwApproval}</strong>
                    </div>
                  )}
                  {gwSaleDate && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--muted)' }}>Payment Date/Time</span>
                      <strong>{gwSaleDate}</strong>
                    </div>
                  )}
                  {gwCardType && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--muted)' }}>Card Type</span>
                      <strong>{gwCardType}</strong>
                    </div>
                  )}
                  {gwAction && (
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: 'var(--muted)' }}>Action Code</span>
                      <strong>{gwAction}</strong>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          <hr className="divider" />

          {/* Items grouped by seller */}
          {order.items && order.items.length > 0 && (() => {
            const groups = groupBySeller(order.items);
            const multi = groups.length > 1;
            return (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 10, color: 'var(--muted)' }}>
                  ITEMS ORDERED
                </div>
                {multi && (
                  <div style={{
                    background: '#fffbeb', border: '1px solid #fcd34d',
                    borderRadius: 8, padding: '10px 12px', marginBottom: 14, fontSize: 13,
                  }}>
                    <span style={{ fontWeight: 700, color: '#92400e' }}>
                      🏪 {groups.length} sellers
                    </span>
                    <span style={{ color: '#78350f' }}>
                      {' '}— each seller will deliver their products to you separately under the same order reference.
                    </span>
                  </div>
                )}
                {groups.map((g, gi) => (
                  <div key={gi} style={{ marginBottom: multi ? 16 : 0 }}>
                    {multi && (
                      <div style={{
                        fontSize: 12, fontWeight: 700, color: 'var(--primary)',
                        textTransform: 'uppercase', letterSpacing: '0.05em',
                        padding: '6px 0', borderBottom: '2px solid var(--primary)',
                        marginBottom: 8,
                      }}>
                        🏪 {g.sellerName}
                      </div>
                    )}
                    {g.items.map((item, i) => (
                      <div key={i} style={{
                        display: 'flex', justifyContent: 'space-between',
                        fontSize: 14, padding: '6px 0', borderBottom: '1px solid var(--border)',
                      }}>
                        <div>
                          <div style={{ fontWeight: 600 }}>
                            {item.product_name || item.productName || item.name || `Product #${item.product_id || item.productId}`}
                          </div>
                          <div style={{ color: 'var(--muted)', fontSize: 12 }}>
                            {formatPrice(item.price, item.currency)} × {item.quantity}
                          </div>
                        </div>
                        <div style={{ fontWeight: 700 }}>
                          {formatPrice((item.price || 0) * (item.quantity || 1), item.currency)}
                        </div>
                      </div>
                    ))}
                    {multi && (
                      <div style={{
                        display: 'flex', justifyContent: 'flex-end',
                        fontSize: 13, color: 'var(--muted)', padding: '6px 0',
                      }}>
                        Subtotal: <strong style={{ marginLeft: 6 }}>{formatPrice(g.subtotal, g.items[0]?.currency || 'INR')}</strong>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Courier + Total */}
          {(() => {
            const orderCur = (order.items?.[0]?.currency) || order.currency || 'INR';
            const courier = parseFloat(order.courier_amount || 0);
            const total = parseFloat(order.total_amount || order.total || order.totalAmount || 0);
            const fMode = (order.fulfillment_mode || 'DELIVERY').toUpperCase();
            return (
              <>
                {fMode && fMode !== 'DELIVERY' && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6, color: 'var(--muted)' }}>
                    <span>Fulfillment</span>
                    <strong style={{ color: 'var(--text)' }}>{fMode === 'PICKUP' ? '🏪 Pickup' : '📦 Parcel/To-Go'}</strong>
                  </div>
                )}
                {courier > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
                    <span style={{ color: 'var(--muted)' }}>Courier</span>
                    <strong>{formatPrice(courier, orderCur)}</strong>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 800, fontSize: 18 }}>
                  <span>Total</span>
                  <span>{formatPrice(total, orderCur)}</span>
                </div>
              </>
            );
          })()}
        </div>
      ) : null}

      {/* ToucanPay — Pay Now (hidden if payment already successful) */}
      {order && order.payment_status !== 'paid' && paymentResult !== 'success' && (
        <div className="card" style={{ padding: 20, marginBottom: 16, border: '2px solid #6366f1' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <span style={{ fontSize: 28 }}>💳</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15 }}>Pay Online</div>
              <div style={{ fontSize: 13, color: 'var(--muted)' }}>UPI · Cards · Net Banking via ToucanPay</div>
            </div>
          </div>
          {payError && (
            <div className="alert alert-error" style={{ marginBottom: 12, fontSize: 13 }}>{payError}</div>
          )}
          <button
            className="btn btn-primary btn-full"
            disabled={payLoading}
            onClick={async () => {
              setPayLoading(true);
              setPayError('');
              try {
                const result = await initiateToucanPayment({
                  orderRef:   ref,
                  buyerName:  location.state?.buyerName  || order.buyer_name  || '',
                  buyerPhone: location.state?.buyerPhone || order.buyer_phone || '',
                  buyerEmail: location.state?.email      || '',
                });
                if (result && result.paymentUrl) {
                  window.location.href = result.paymentUrl;
                } else {
                  setPayError(result?.error || 'Could not get payment link. Please try again.');
                }
              } catch (err) {
                setPayError(err.message || 'Payment initiation failed.');
              } finally {
                setPayLoading(false);
              }
            }}
            style={{ background: '#6366f1', borderColor: '#6366f1', fontWeight: 700, fontSize: 15 }}
          >
            {payLoading ? 'Connecting to ToucanPay…' : `Pay ${formatPrice(order.total_amount || order.totalAmount || 0, order.items?.[0]?.currency || 'INR')} Now`}
          </button>
          <div style={{ fontSize: 11, color: 'var(--muted)', textAlign: 'center', marginTop: 8 }}>
            You will be redirected to ToucanPay's secure payment page
          </div>
        </div>
      )}

      {/* Receipt actions */}
      {order && (() => {
        const sellerIds = [...new Set((order.items || []).map(i => i.seller_id || i.sellerId).filter(Boolean))];
        const receiptTitle = sellerIds.length === 1
          ? ((order.items || [])[0]?.seller_name || (order.items || [])[0]?.sellerName || 'Receipt')
          : 'Order Receipt';
        return (
        <div className="card" style={{ padding: 16, marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>🧾 {receiptTitle}</div>

          {/* Print / Save PDF */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
            <button
              className="btn btn-outline"
              onClick={() => printOrderReceipt(order, ref)}
              style={{ flex: 1, minWidth: 130 }}
            >
              🖨️ Print Receipt
            </button>
            <button
              className="btn btn-outline"
              onClick={() => {
                const win = window.open('', '_blank', 'width=700,height=600');
                win.document.write(buildReceiptHtml(order, ref));
                win.document.close();
                win.focus();
                setTimeout(() => win.print(), 400);
              }}
              style={{ flex: 1, minWidth: 130 }}
            >
              💾 Save as PDF
            </button>
          </div>

          {/* Email receipt — hidden for online payments (auto-sent from backend) */}
          {order.payment_status !== 'paid' && paymentResult !== 'success' && (
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>📧 Email Receipt</div>
            {emailStatus === 'sent' ? (
              <div style={{ color: '#16a34a', fontSize: 13, fontWeight: 600 }}>
                ✅ Receipt sent to {emailInput}
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <input
                  type="email"
                  className="form-control"
                  placeholder="your@email.com"
                  value={emailInput}
                  onChange={e => { setEmailInput(e.target.value); setEmailStatus(null); }}
                  style={{ flex: 1, minWidth: 180 }}
                />
                <button
                  className="btn btn-primary"
                  disabled={emailSending || !emailInput.trim()}
                  onClick={async () => {
                    if (!emailInput.trim()) return;
                    setEmailSending(true); setEmailStatus(null);
                    try {
                      await sendOrderReceipt(emailInput.trim(), order, ref);
                      setEmailStatus('sent');
                    } catch {
                      setEmailStatus('error');
                    } finally {
                      setEmailSending(false);
                    }
                  }}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  {emailSending ? 'Sending…' : 'Send'}
                </button>
              </div>
            )}
            {emailStatus === 'error' && (
              <div style={{ color: '#dc2626', fontSize: 12, marginTop: 6 }}>
                Failed to send email. Please try again.
              </div>
            )}
          </div>
          )}
          {/* Auto-email confirmation for online payments */}
          {(order.payment_status === 'paid' || paymentResult === 'success') && order.buyer_email && (
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12, fontSize: 13, color: '#16a34a', fontWeight: 600 }}>
              📧 Receipt emailed automatically to {order.buyer_email}
            </div>
          )}
        </div>
        );
      })()}

      {/* Navigation */}
      <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', paddingBottom: 32 }}>
        <Link to="/track" className="btn btn-outline">Track Another Order</Link>
        <Link to="/" className="btn btn-primary">Continue Shopping</Link>
      </div>
    </div>
  );
}
